import type {
  AxiosError,
  AxiosInstance,
  InternalAxiosRequestConfig,
} from 'axios'
import axios from 'axios'

import { logger } from '@myorg/type-lib'
import { refreshCoordinator } from './refreshCoordinator'

export interface AuthInterceptorHooks {
  isTokenAuth: () => boolean
  getAccessToken: () => Promise<string | null>
  refreshAuth: (baseURL: string) => Promise<{ access_token?: string } | null | void>
  logout: () => Promise<void>
}

const defaultAuthHooks: AuthInterceptorHooks = {
  isTokenAuth: () => false,
  getAccessToken: async () => null,
  refreshAuth: async (baseURL: string) => {
    await axios.post(
      `${baseURL}/api/client/refresh`,
      {},
      {
        withCredentials: true,
        _skipGlobalError: true,
      }
    )

    return null
  },
  logout: async () => undefined,
}

export function setupAuthInterceptor(
  api: AxiosInstance,
  baseURL: string,
  hooks: Partial<AuthInterceptorHooks> = {}
) {
  const authHooks = {
    ...defaultAuthHooks,
    ...hooks,
  }

  logger.log('SETUP INTERCEPTOR', baseURL)
  logger.log('AUTH INTERCEPTOR VERSION 20260623')

  api.interceptors.request.use(async config => {
    if (authHooks.isTokenAuth()) {
      const token = await authHooks.getAccessToken()

      if (token) {
        config.headers = config.headers ?? {}
        config.headers.Authorization = `Bearer ${token}`
      }

      config.withCredentials = false
    } else {
      config.withCredentials = true
    }

    return config
  })

  api.interceptors.response.use(
    res => {
      logger.log('[Interceptor] success', res.config.url, res.status)
      return res
    },

    async (error: AxiosError) => {
      logger.error('[Interceptor] axios error', {
        url: error.config?.url,
        method: error.config?.method,
        message: error.message,
        code: error.code,
        status: error.response?.status,
        data: error.response?.data,
      })

      const original = error.config as InternalAxiosRequestConfig | undefined

      if (!original) {
        logger.warn('[Interceptor] No original config')
        throw error
      }

      if (
        original._skipGlobalError ||
        original.url?.includes('/api/client/refresh') ||
        original.url?.includes('/api/server/oauth2/token')
      ) {
        logger.log('[Interceptor] Skip refresh handling', original.url)
        throw error
      }

      if (error.response?.status !== 401) {
        logger.log('[Interceptor] Ignore non-401', error.response?.status)
        throw error
      }

      logger.log('[Interceptor] Handle 401', original.url)

      if (original._retry) {
        logger.warn('[Interceptor] Already retried', original.url)
        throw error
      }

      original._retry = true

      if (!refreshCoordinator.beginRefresh()) {
        logger.log('[Interceptor] waiting refresh')
        await refreshCoordinator.waitForRefresh()
        logger.log('[Interceptor] retry after waiting', original.url)
        return api(original)
      }

      try {
        logger.log('[Interceptor] start refresh')

        const tokens = await authHooks.refreshAuth(baseURL)

        logger.log('[Interceptor] refresh success')

        if (tokens?.access_token) {
          original.headers = original.headers ?? {}
          original.headers.Authorization = `Bearer ${tokens.access_token}`
        }

        refreshCoordinator.resolveRefresh()

        logger.log('[Interceptor] retry original request', original.url)
        return api(original)
      } catch (err) {
        logger.error('[Interceptor] refresh failed', err)
        refreshCoordinator.rejectRefresh(err as AxiosError)

        logger.warn('[Interceptor] logout after refresh failure')
        await authHooks.logout()

        throw err
      }
    }
  )
}
